import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Save, Pencil, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useNoIndex } from "@/hooks/useCanonicalTag";

const SECTION_LABELS: Record<string, string> = {
  hero: "Hero (Topo)",
  social_proof: "Prova Social",
  problem: "Problema",
  solution: "Solução",
  how_it_works: "Como Funciona",
  pricing: "Planos e Preços",
  faq: "FAQ",
  cta: "CTA Final",
};

export default function SalesPageEditor() {
  useNoIndex();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingSection, setEditingSection] = useState<any>(null);
  const [formData, setFormData] = useState<any>(null);

  const { data: sections = [], isLoading } = useQuery({
    queryKey: ["admin-sales-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_page_sections")
        .select("*")
        .order("section_key");
      if (error) throw error;
      return data || [];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase
        .from("sales_page_sections")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sales-sections"] });
      queryClient.invalidateQueries({ queryKey: ["sales-page-sections"] });
      toast({ title: "Seção atualizada!" });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    },
  });

  const toggleVisibility = (section: any) => {
    updateMutation.mutate({
      id: section.id,
      updates: { is_visible: !section.is_visible },
    });
  };

  const openEditor = (section: any) => {
    setEditingSection(section);
    setFormData({
      title: section.title || "",
      subtitle: section.subtitle || "",
      content: section.content || {},
    });
  };

  const saveSection = () => {
    if (!editingSection || !formData) return;
    updateMutation.mutate({
      id: editingSection.id,
      updates: {
        title: formData.title,
        subtitle: formData.subtitle,
        content: formData.content,
      },
    });
  };

  if (isLoading) return <CandidateLayout><div className="p-8 text-center text-muted-foreground">Carregando...</div></CandidateLayout>;

  return (
    <CandidateLayout>
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Editor da LP "Para Empresas"</h1>
      <p className="text-muted-foreground mb-8">Edite o conteúdo de cada seção da página de vendas para empresas.</p>

      <div className="space-y-4">
        {sections.map((section: any) => (
          <Card key={section.id}>
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <CardTitle className="text-base font-semibold">
                {SECTION_LABELS[section.section_key] || section.section_key}
              </CardTitle>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleVisibility(section)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title={section.is_visible ? "Visível" : "Oculto"}
                >
                  {section.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-destructive" />}
                </button>
                <Button size="sm" variant="outline" onClick={() => openEditor(section)}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              <p className="text-sm text-muted-foreground truncate">{section.title || "Sem título"}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editingSection} onOpenChange={(open) => !open && setEditingSection(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Editar: {editingSection && (SECTION_LABELS[editingSection.section_key] || editingSection.section_key)}
            </DialogTitle>
          </DialogHeader>

          {formData && editingSection && (
            <div className="space-y-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Subtítulo</Label>
                <Textarea
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  rows={2}
                />
              </div>

              {/* FAQ editor */}
              {editingSection.section_key === "faq" && (
                <FAQEditor
                  questions={formData.content?.questions || []}
                  onChange={(questions: any[]) =>
                    setFormData({ ...formData, content: { ...formData.content, questions } })
                  }
                />
              )}

              {/* Pricing editor */}
              {editingSection.section_key === "pricing" && (
                <PricingEditor
                  plans={formData.content?.plans || []}
                  onChange={(plans: any[]) =>
                    setFormData({ ...formData, content: { ...formData.content, plans } })
                  }
                />
              )}

              {/* Generic items editors */}
              {editingSection.section_key === "social_proof" && (
                <MetricsEditor
                  metrics={formData.content?.metrics || []}
                  onChange={(metrics: any[]) =>
                    setFormData({ ...formData, content: { ...formData.content, metrics } })
                  }
                />
              )}

              {editingSection.section_key === "problem" && (
                <ItemsEditor
                  label="Problemas"
                  items={formData.content?.problems || []}
                  fields={["title", "description"]}
                  onChange={(problems: any[]) =>
                    setFormData({ ...formData, content: { ...formData.content, problems } })
                  }
                />
              )}

              {editingSection.section_key === "solution" && (
                <ItemsEditor
                  label="Soluções"
                  items={formData.content?.solutions || []}
                  fields={["title", "description", "highlight"]}
                  onChange={(solutions: any[]) =>
                    setFormData({ ...formData, content: { ...formData.content, solutions } })
                  }
                />
              )}

              {editingSection.section_key === "how_it_works" && (
                <ItemsEditor
                  label="Passos"
                  items={formData.content?.steps || []}
                  fields={["step", "title", "description"]}
                  onChange={(steps: any[]) =>
                    setFormData({ ...formData, content: { ...formData.content, steps } })
                  }
                />
              )}

              {editingSection.section_key === "hero" && (
                <div className="space-y-3">
                  <div>
                    <Label>Badge</Label>
                    <Input
                      value={formData.content?.badge || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, content: { ...formData.content, badge: e.target.value } })
                      }
                    />
                  </div>
                  <ItemsEditor
                    label="Stats"
                    items={formData.content?.stats || []}
                    fields={["value", "label"]}
                    onChange={(stats: any[]) =>
                      setFormData({ ...formData, content: { ...formData.content, stats } })
                    }
                  />
                </div>
              )}

              {editingSection.section_key === "cta" && (
                <div className="space-y-3">
                  <div>
                    <Label>Texto do botão</Label>
                    <Input
                      value={formData.content?.button_text || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, content: { ...formData.content, button_text: e.target.value } })
                      }
                    />
                  </div>
                  <div>
                    <Label>Texto secundário</Label>
                    <Input
                      value={formData.content?.secondary_text || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, content: { ...formData.content, secondary_text: e.target.value } })
                      }
                    />
                  </div>
                </div>
              )}

              <Button onClick={saveSection} disabled={updateMutation.isPending} className="w-full">
                <Save className="h-4 w-4 mr-2" />
                {updateMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </CandidateLayout>
  );
}

// Sub-editors

function FAQEditor({ questions, onChange }: { questions: any[]; onChange: (q: any[]) => void }) {
  const update = (i: number, field: string, value: string) => {
    const copy = [...questions];
    copy[i] = { ...copy[i], [field]: value };
    onChange(copy);
  };
  const remove = (i: number) => onChange(questions.filter((_, idx) => idx !== i));
  const add = () => onChange([...questions, { question: "", answer: "" }]);

  return (
    <div className="space-y-3">
      <Label>Perguntas e Respostas</Label>
      {questions.map((q: any, i: number) => (
        <div key={i} className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Pergunta {i + 1}</span>
            <button onClick={() => remove(i)} className="text-destructive hover:text-destructive/80">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <Input value={q.question} onChange={(e) => update(i, "question", e.target.value)} placeholder="Pergunta" />
          <Textarea value={q.answer} onChange={(e) => update(i, "answer", e.target.value)} placeholder="Resposta" rows={2} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar pergunta
      </Button>
    </div>
  );
}

function PricingEditor({ plans, onChange }: { plans: any[]; onChange: (p: any[]) => void }) {
  const update = (i: number, field: string, value: any) => {
    const copy = [...plans];
    copy[i] = { ...copy[i], [field]: value };
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      <Label>Planos</Label>
      {plans.map((plan: any, i: number) => (
        <div key={i} className="border rounded-lg p-3 space-y-2">
          <div className="flex gap-2">
            <Input value={plan.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Nome" className="flex-1" />
            <Input value={plan.price} onChange={(e) => update(i, "price", e.target.value)} placeholder="Preço" className="w-24" />
            <Input value={plan.period} onChange={(e) => update(i, "period", e.target.value)} placeholder="Período" className="w-24" />
          </div>
          <Input value={plan.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Descrição" />
          <Input value={plan.cta || ""} onChange={(e) => update(i, "cta", e.target.value)} placeholder="Texto do botão" />
          <Textarea
            value={(plan.features || []).join("\n")}
            onChange={(e) => update(i, "features", e.target.value.split("\n"))}
            placeholder="Features (1 por linha)"
            rows={3}
          />
          <div className="flex items-center gap-2">
            <Switch checked={plan.popular || false} onCheckedChange={(v) => update(i, "popular", v)} />
            <span className="text-xs text-muted-foreground">Mais popular</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MetricsEditor({ metrics, onChange }: { metrics: any[]; onChange: (m: any[]) => void }) {
  const update = (i: number, field: string, value: any) => {
    const copy = [...metrics];
    copy[i] = { ...copy[i], [field]: field === "target" ? Number(value) : value };
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      <Label>Métricas</Label>
      {metrics.map((m: any, i: number) => (
        <div key={i} className="border rounded-lg p-3 space-y-2">
          <div className="flex gap-2">
            <Input type="number" value={m.target} onChange={(e) => update(i, "target", e.target.value)} placeholder="Valor" className="w-24" />
            <Input value={m.suffix} onChange={(e) => update(i, "suffix", e.target.value)} placeholder="Sufixo" className="w-16" />
            <Input value={m.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label" className="flex-1" />
          </div>
          <Input value={m.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Descrição" />
        </div>
      ))}
    </div>
  );
}

function ItemsEditor({
  label,
  items,
  fields,
  onChange,
}: {
  label: string;
  items: any[];
  fields: string[];
  onChange: (items: any[]) => void;
}) {
  const update = (i: number, field: string, value: string) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: value };
    onChange(copy);
  };
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => {
    const empty: any = {};
    fields.forEach((f) => (empty[f] = ""));
    onChange([...items, empty]);
  };

  const fieldLabels: Record<string, string> = {
    title: "Título",
    description: "Descrição",
    highlight: "Destaque",
    step: "Passo",
    value: "Valor",
    label: "Label",
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      {items.map((item: any, i: number) => (
        <div key={i} className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">{label} {i + 1}</span>
            <button onClick={() => remove(i)} className="text-destructive hover:text-destructive/80">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {fields.map((field) => (
            <div key={field}>
              <span className="text-xs text-muted-foreground">{fieldLabels[field] || field}</span>
              {field === "description" ? (
                <Textarea value={item[field] || ""} onChange={(e) => update(i, field, e.target.value)} rows={2} />
              ) : (
                <Input value={item[field] || ""} onChange={(e) => update(i, field, e.target.value)} />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
      </Button>
    </div>
  );
}
